import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "src/@/components/ui/dialog";
import { TAREFAS_CONFIG_DESIGN as D } from './TarefasConfig.design';
import Iconify from 'src/components/Iconify';

const CORES_PREDEFINIDAS = [
  { valor: '#ef4444', nome: 'Vermelho' },
  { valor: '#f59e0b', nome: 'Amarelo' },
  { valor: '#22c55e', nome: 'Verde' },
  { valor: '#3b82f6', nome: 'Azul' },
  { valor: '#8b5cf6', nome: 'Violeta' },
  { valor: '#ec4899', nome: 'Rosa' },
  { valor: '#f97316', nome: 'Laranja' },
  { valor: '#06b6d4', nome: 'Ciano' },
  { valor: '#6b7280', nome: 'Cinza' },
  { valor: '#7C3AED', nome: 'Roxo' },
];

export default function ModalWorkflow({
  open,
  onClose,
  onSubmit,
  action,
  workflow,
}) {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [estagios, setEstagios] = useState([{ nome: '', cor: '#6b7280' }]);
  const [saving, setSaving] = useState(false);

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    if (workflow && action === 'Editar') {
      setNome(workflow.nome || '');
      setDescricao(workflow.descricao || '');
      setEstagios(workflow.estagios?.length > 0
        ? workflow.estagios.map(e => ({ ...e }))
        : [{ nome: '', cor: '#6b7280' }]
      );
    } else {
      setNome('');
      setDescricao('');
      setEstagios([{ nome: '', cor: '#6b7280' }]);
    }
  }, [workflow, action, open]);

  const handleAddEstagio = () => {
    setEstagios([...estagios, { nome: '', cor: '#7C3AED' }]);
  };

  const handleRemoveEstagio = (index) => {
    if (estagios.length <= 1) return;
    setEstagios(estagios.filter((_, i) => i !== index));
  };

  const handleEstagioChange = (index, field, value) => {
    const updated = [...estagios];
    updated[index] = { ...updated[index], [field]: value };
    setEstagios(updated);
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const reordered = [...estagios];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);
    dragItem.current = null;
    dragOverItem.current = null;
    setEstagios(reordered);
  };

  const handleSubmit = async () => {
    if (!nome.trim()) return;
    const validEstagios = estagios.filter(e => e.nome.trim());
    if (validEstagios.length === 0) return;

    setSaving(true);
    try {
      await onSubmit({
        nome: nome.trim(),
        descricao: descricao.trim(),
        estagios: validEstagios.map((e, i) => ({ ...e, ordem: i })),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const isValid = nome.trim() && estagios.some(e => e.nome.trim());
  const estagiosNomeados = estagios.filter(e => e.nome.trim());

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto bg-white border border-[#e7e7ec] rounded-[12px] p-[26px]">
        <DialogHeader>
          <DialogTitle className={D.dialogTitle}>
            {action === 'Criar' ? 'Novo workflow' : 'Editar workflow'}
          </DialogTitle>
          <DialogDescription className={D.dialogDesc}>
            {action === 'Criar'
              ? 'Crie um caminho de estágios para organizar as tarefas da equipe.'
              : 'Ajuste o nome e os estágios deste fluxo.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          <div className={D.field}>
            <label className={D.label}>Nome</label>
            <input
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ex: Suporte, Desenvolvimento"
              className={D.input}
            />
            <p className={D.hint}>Como este fluxo aparece na lista de tarefas.</p>
          </div>

          <div className={D.field}>
            <label className={D.label}>
              Descrição <span className="font-medium text-[#a1a1aa]">(opcional)</span>
            </label>
            <input
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="Quando a equipe deve usar este fluxo"
              className={D.input}
            />
            <p className={D.hint}>Ajuda a equipe a saber quando escolher este workflow.</p>
          </div>

          <div className={D.field}>
            <label className={D.label}>Estágios</label>
            <p className={`${D.hint} mt-0 mb-2.5`}>
              A ordem define o caminho da tarefa. Arraste para reorganizar.
            </p>

            <div className="flex flex-col gap-2.5">
              {estagios.map((estagio, index) => (
                <div
                  key={index}
                  className={D.stageRow}
                  draggable
                  onDragStart={() => { dragItem.current = index; }}
                  onDragEnter={() => { dragOverItem.current = index; }}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <Iconify icon="eva:menu-outline" width={16} className="text-[#d4d4d8] flex-shrink-0" />
                  <span className={D.stageIndex}>{index + 1}</span>

                  <input
                    value={estagio.nome}
                    onChange={e => handleEstagioChange(index, 'nome', e.target.value)}
                    placeholder="Nome do estágio"
                    className={`${D.input} flex-1 py-2`}
                  />

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {CORES_PREDEFINIDAS.slice(0, 5).map((c) => (
                      <button
                        key={c.valor}
                        type="button"
                        onClick={() => handleEstagioChange(index, 'cor', c.valor)}
                        className={D.colorSwatch}
                        style={{
                          backgroundColor: c.valor,
                          boxShadow: estagio.cor === c.valor ? '0 0 0 2px #6d28d9' : 'none',
                        }}
                        title={c.nome}
                      />
                    ))}
                    <input
                      type="color"
                      value={estagio.cor}
                      onChange={e => handleEstagioChange(index, 'cor', e.target.value)}
                      className="w-5 h-5 rounded-md cursor-pointer border border-[#d4d4d8] appearance-none bg-transparent flex-shrink-0"
                      style={{ backgroundColor: estagio.cor }}
                      title="Cor personalizada"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveEstagio(index)}
                    disabled={estagios.length <= 1}
                    className={estagios.length <= 1 ? D.iconButtonDisabled : D.iconButtonDanger}
                    title="Remover estágio"
                  >
                    <Iconify icon="eva:trash-2-outline" width={14} />
                  </button>
                </div>
              ))}
            </div>

            <button type="button" onClick={handleAddEstagio} className={`${D.link} mt-3`}>
              Adicionar estágio
            </button>
          </div>

          <div className={D.field}>
            <p className={D.label}>Prévia</p>
            <div className={D.previewShell}>
              {estagiosNomeados.map((estagio, i) => (
                <div key={i} className="flex items-center gap-1">
                  <span
                    className={D.previewChip}
                    style={{ backgroundColor: `${estagio.cor}20`, color: estagio.cor }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: estagio.cor }}
                    />
                    {estagio.nome}
                  </span>
                  {i < estagiosNomeados.length - 1 && (
                    <span className="text-[#a1a1aa] text-[12px] mx-0.5">→</span>
                  )}
                </div>
              ))}
              {estagiosNomeados.length === 0 && (
                <span className="text-[12px] text-[#a1a1aa]">Adicione estágios acima para ver o caminho.</span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className={D.dialogFooter}>
          <button type="button" onClick={onClose} className={D.ghostButton}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !isValid}
            className={D.primaryButton}
          >
            {action === 'Criar' ? 'Criar' : 'Salvar'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
